/**
 * Single source of truth for the private-office proposal pricing math.
 *
 * The client pays in credits: `ceil(monthlyPrice / FIXED_CREDIT_PRICE)` credits
 * charged at the fixed credit price. Woba's gain is the space take over the
 * partner's monthly price plus whatever the credit rounding adds on top of it;
 * margin is that gain over the total charged.
 *
 * Margin is always DERIVED from the final price — it is never an input. Manual
 * margin editing was removed per commercial policy (PED-12647); the official
 * calculation policy is still pending, so negative extra values are never
 * applied as automatic discounts: a room whose derived margin already exceeds
 * the target keeps its base price.
 *
 * The credit price is FIXED at R$ 80 by the same commercial policy — it is not
 * an input either. The old default/cap pair collapsed into this constant.
 */

export const FIXED_CREDIT_PRICE = 80;
export const DEFAULT_SPACE_TAKE_PCT = 16;

export interface CreditPricing {
  credits: number;
  clientPrice: number;
  extraValue: number;
  extraCredits: number;
  totalCredits: number;
  totalPrice: number;
  margin: number;
}

export function calculateCreditPricing(
  monthlyPrice: number,
  takePct: number = DEFAULT_SPACE_TAKE_PCT,
  extraValue: number = 0,
): CreditPricing {
  const take = takePct / 100;
  const credits = Math.ceil(monthlyPrice / FIXED_CREDIT_PRICE);
  const clientPrice = FIXED_CREDIT_PRICE * credits;

  const appliedExtraValue = Math.max(extraValue, 0);
  const extraCredits =
    appliedExtraValue > 0
      ? Math.ceil(appliedExtraValue / FIXED_CREDIT_PRICE)
      : 0;
  const totalPrice = clientPrice + extraCredits * FIXED_CREDIT_PRICE;
  const margin = Number(
    (
      ((monthlyPrice * take + (totalPrice - monthlyPrice)) / totalPrice) *
      100
    ).toFixed(2),
  );

  return {
    credits,
    clientPrice,
    extraValue: appliedExtraValue,
    extraCredits,
    totalCredits: credits + extraCredits,
    totalPrice,
    margin,
  };
}
